import axios, { InternalAxiosRequestConfig } from 'axios'
import { supabase } from '../supabase'
import { reloadToLogin } from '../reload-to-login'

import {
  UserResponse,
  VerifyPasswordRequest,
  VerifyPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse
} from './generated/models'

export type User = UserResponse

export interface SupabaseUser extends User {
  app_metadata: {
    provider?: string
    [key: string]: any
  }
  user_metadata: {
    [key: string]: any
  }
  created_at: string
  updated_at: string
  phone?: string | null
  confirmed_at?: string | null
  email_confirmed_at?: string | null
  banned_until?: string | null
  aud: string
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Configure axios instance for all API calls
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Check if this is during TOTP verification phase
      // If user is authenticated but TOTP not verified, this 401 is expected
      const { data: { session } } = await supabase.auth.getSession()
      const isLoggedInButNotMFAVerified = !!session && (
        window.location.hash.includes('/auth/totp-verify') ||
        window.location.hash.includes('/auth/complete-invite')
      )

      if (isLoggedInButNotMFAVerified) {
        console.log('[API] 401 during TOTP verification - this is expected, not signing out')
        return Promise.reject(error)
      }

      // Handle 401 Unauthorized responses by redirecting to login
      console.log('[API] 401 Unauthorized detected, redirecting to login page')

      // Clear the session from Supabase
      try {
        await supabase.auth.signOut()
      } catch (signOutError) {
        console.error('[API] Error during signOut:', signOutError)
      }

      reloadToLogin()

      return Promise.reject(error)
    }

    // FF-3799 (H5): a 403 with MFA_REQUIRED means the API rejected a single-factor session. Send the
    // user to the login page, which is where admin-ui hosts the second-factor step: login.tsx renders
    // the TOTP challenge/enrolment after a fresh sign-in (in handleSubmit). The code comes from the
    // canonical error envelope (errors[0].code); the hash guard avoids a redirect loop.
    //
    // Known limitation (follow-up): login.tsx has no mount effect that inspects an already-present
    // aal1 session, so an admin who is already signed in and gets bounced here must re-enter their
    // password before the TOTP step appears — not a lockout, but a rough edge. The clean fix is a
    // mount effect keyed on the session's assurance level (mirroring ProtectedRoute's mfa_required
    // path). Raised for frontend to pick up.
    if (
      error.response?.status === 403 &&
      error.response?.data?.errors?.[0]?.code === 'MFA_REQUIRED' &&
      !window.location.hash.includes('/auth/login')
    ) {
      reloadToLogin()
      return Promise.reject(error)
    }

    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      // Trigger a session refresh to update ban status
      const auth = (window as any).__SUPABASE_AUTH_PROVIDER__
      if (auth?.checkBanStatus) {
        await auth.checkBanStatus()
      }
    }
    return Promise.reject(error)
  }
)

export const getPlatformAdminUsers = async (): Promise<User[]> => {
  const response = await api.get('/api/v1/users?role=backoffice.admin')
  return response.data.items
}

export const getPartnerUsers = async (partnerId: string): Promise<User[]> => {
  const response = await api.get(`/api/v1/users?entityId=${partnerId}`)
  return response.data.items
}

export const getUsers = async (): Promise<User[]> => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await axios.get(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/admin/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  })

  return response.data
}

export const getUserProfile = async () => {
  const response = await api.get('/api/v1/users/profile')
  return response.data
}

export const deleteTotpDevice = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')
  await api.delete(`/api/v1/users/${user.id}/totp`)
}

export const inviteUser = async (data: {
  email: string
  firstName: string
  lastName: string
  userRole?: string
  role?: string
  entityType?: string
  entityId?: string
  redirectUrl?: string
}): Promise<void> => {
  await api.post('/api/v1/users', data)
}

export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/api/v1/users/${userId}`)
}

export const getUserById = async (userId: string): Promise<User> => {
  const response = await api.get(`/api/v1/users/${userId}`)
  return response.data
}

export const resendInvite = async (userId: string, redirectUrl?: string): Promise<void> => {
  const params = redirectUrl ? { redirectUrl } : undefined
  await api.post(`/api/v1/users/${userId}/resend-invite`, undefined, { params })
}

/**
 * Verifies the current user's password
 * @param password The password to verify
 * @returns A promise that resolves to a VerifyPasswordResponse
 */
export const verifyPassword = async (password: string): Promise<VerifyPasswordResponse> => {
  const request: VerifyPasswordRequest = { password }
  const response = await api.post('/api/v1/users/verify-password', request)
  return response.data
}

/**
 * Changes the current user's password
 * @param currentPassword The current password
 * @param newPassword The new password
 * @returns A promise that resolves to a ChangePasswordResponse
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
  const request: ChangePasswordRequest = { currentPassword, newPassword }
  const response = await api.post('/api/v1/users/change-password', request)
  return response.data
}

export interface AssignableRole {
  role: string
  displayName: string
}

export const getAssignableRoles = async (entityType?: string): Promise<AssignableRole[]> => {
  const params = entityType ? { entityType } : {}
  const response = await api.get('/api/v1/users/assignable-roles', { params })
  return response.data
}

export { api }
